import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import { customFetch } from '../../apiClient';

const GSTR2Report = () => {
  const [reportType, setReportType] = useState("B2B");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(
    () => new Date().toISOString().split("T")[0]
  );
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");

  const reportTabs = [
    { id: "B2B",     label: "B2B (Registered)",       icon: "bi-building-check",      color: "#3b82f6",
      hint: "Registered vendors (GSTIN wale) se purchases — ITC claim hogi" },
    { id: "B2BUR",   label: "B2BUR (Unregistered)",    icon: "bi-building-x",          color: "#64748b",
      hint: "Bina GSTIN wale vendors se purchases — ITC nahi milegi" },
    { id: "HSN",     label: "HSN Summary",             icon: "bi-tags",                color: "#f59e0b",
      hint: "Item wise purchase summary — HSN code ke saath" },
    { id: "CDNR",    label: "Debit Notes (Reg.)",      icon: "bi-arrow-return-left",   color: "#ef4444",
      hint: "Registered vendors ko returns (Debit Notes) — ITC reversal hogi" },
    { id: "CDNUR",   label: "Debit Notes (Unreg.)",    icon: "bi-arrow-counterclockwise", color: "#8b5cf6",
      hint: "Unregistered vendors ko returns" },
    { id: "SUMMARY", label: "Purchase Summary",        icon: "bi-bar-chart-line",      color: "#10b981",
      hint: "Month wise purchase vs return summary" },
  ];

  useEffect(() => {
    setReportData([]);
    setError("");
  }, [reportType]);

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    setReportData([]);
    try {
      const params = new URLSearchParams({ reportType, fromDate, toDate });
      const res = await customFetch(`/GSTR2Report/generate?${params}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to fetch");
      }
      const data = await res.json();
      setReportData(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Summary cards — B2B/CDNR mein double count avoid
  const summary = useMemo(() => {
    let taxable = 0, cgst = 0, sgst = 0, igst = 0, totalValue = 0;
    const uniqueDocs = new Set();

    reportData.forEach(row => {
      taxable += Number(row.Taxable_Value         || 0);
      cgst    += Number(row.Central_Tax_Amount    || 0);
      sgst    += Number(row.State_UT_Tax_Amount   || 0);
      igst    += Number(row.Integrated_Tax_Amount || 0);

      // Invoice/Note level unique count ke liye
      const docKey = row.Invoice_Number || row.Note_Number || null;
      if (docKey) {
        if (!uniqueDocs.has(docKey)) {
          uniqueDocs.add(docKey);
          totalValue += Number(row.Invoice_Value || row.Note_Value || 0);
        }
      } else {
        // HSN / SUMMARY: direct add
        totalValue += Number(
          row.Total_Value         ||
          row.Total_Purchase_Value ||
          row.Net_Purchase_Value  || 0
        );
      }
    });

    const itcTotal = (
      Number(reportData.reduce((s, r) => s + Number(r.ITC_CGST      || 0), 0)) +
      Number(reportData.reduce((s, r) => s + Number(r.ITC_SGST      || 0), 0)) +
      Number(reportData.reduce((s, r) => s + Number(r.ITC_IGST      || 0), 0))
    );

    return {
      taxable, cgst, sgst, igst, totalValue, itcTotal,
      docCount: uniqueDocs.size || reportData.length,
    };
  }, [reportData]);

  const exportToExcel = useCallback(() => {
    if (!reportData.length) { alert("No data to export!"); return; }
    const ws = XLSX.utils.json_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, reportType);
    XLSX.writeFile(wb, `GSTR2_${reportType}_${fromDate}_to_${toDate}.xlsx`);
  }, [reportData, reportType, fromDate, toDate]);

  const activeTab = reportTabs.find(t => t.id === reportType);
  const columns   = reportData.length > 0 ? Object.keys(reportData[0]) : [];
  const isNumericCol = (col) =>
    reportData.length > 0 && typeof reportData[0][col] === "number";

  // ITC columns highlight karo
  const isItcCol = (col) =>
    col.startsWith("ITC_") || col.includes("Reversal");

  return (
    <div style={{ padding: 0, fontSize: 12 }}>
      <div style={{
        background: "white", borderRadius: 8,
        boxShadow: "0 1px 10px rgba(0,0,0,0.06)",
        border: "1px solid #e0e6ed", overflow: "hidden"
      }}>

        {/* ── Header ── */}
        <div style={{
          background: activeTab?.color || "#0088cc",
          padding: "10px 16px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          transition: "background 0.3s"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "white" }}>
            <div style={{
              width: 34, height: 34, background: "rgba(255,255,255,0.2)",
              borderRadius: 6, display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: 16
            }}>
              <i className={`bi ${activeTab?.icon || "bi-file-earmark"}`}></i>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>GSTR-2 Purchase Report</div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>
                {activeTab?.label} — ITC & Compliance
              </div>
            </div>
          </div>
          <button
            onClick={exportToExcel}
            disabled={!reportData.length}
            style={{
              padding: "5px 14px", fontSize: 11, fontWeight: 600,
              background: "white", border: "none", borderRadius: 4,
              cursor: reportData.length ? "pointer" : "not-allowed",
              opacity: reportData.length ? 1 : 0.5, color: "#1e293b"
            }}
          >
            <i className="bi bi-file-earmark-excel text-success me-1"></i>
            Export Excel
          </button>
        </div>

        {/* ── Tabs ── */}
        <div style={{
          display: "flex", background: "#f8fafc",
          borderBottom: "1px solid #e0e6ed",
          padding: "0 12px", gap: 4, overflowX: "auto"
        }}>
          {reportTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setReportType(tab.id)}
              style={{
                padding: "9px 12px", border: "none", background: "transparent",
                color: reportType === tab.id ? tab.color : "#64748b",
                fontWeight: 600, fontSize: 11, cursor: "pointer",
                borderBottom: reportType === tab.id
                  ? `2px solid ${tab.color}` : "2px solid transparent",
                whiteSpace: "nowrap", transition: "color 0.2s"
              }}
            >
              <i className={`bi ${tab.icon} me-1`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 14 }}>

          {/* ── Error ── */}
          {error && (
            <div style={{
              padding: "8px 12px", background: "#fee2e2", color: "#dc2626",
              borderRadius: 6, marginBottom: 10, fontSize: 12
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* ── Filter Bar ── */}
          <div style={{
            display: "flex", gap: 8, background: "#f8fafc",
            padding: "10px 12px", borderRadius: 6,
            border: "1px solid #e0e6ed", marginBottom: 12,
            alignItems: "flex-end", flexWrap: "wrap"
          }}>
            {[
              { label: "From Date", val: fromDate, set: setFromDate },
              { label: "To Date",   val: toDate,   set: setToDate   },
            ].map(({ label, val, set }) => (
              <div key={label} style={{ display: "flex", flexDirection: "column" }}>
                <label style={{
                  fontSize: 11, fontWeight: 600, marginBottom: 3, color: "#475569"
                }}>
                  {label}
                </label>
                <input
                  type="date" value={val} onChange={e => set(e.target.value)}
                  style={{
                    padding: "4px 8px", border: "1px solid #cbd5e1",
                    borderRadius: 4, fontSize: 12, height: 30, outline: "none"
                  }}
                />
              </div>
            ))}

            <button
              onClick={fetchReport} disabled={loading}
              style={{
                padding: "0 18px", height: 30,
                background: activeTab?.color || "#0088cc",
                color: "white", border: "none", borderRadius: 4,
                fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
                fontSize: 12, opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? "Loading..." : "View Report"}
            </button>

            {/* Tab hint */}
            <div style={{
              marginLeft: "auto", fontSize: 11, color: "#94a3b8",
              display: "flex", alignItems: "center", gap: 4,
              maxWidth: 300, textAlign: "right"
            }}>
              <i className="bi bi-info-circle"></i>
              {activeTab?.hint}
            </div>
          </div>

          {/* ── Summary Cards ── */}
          {!loading && reportData.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))",
              gap: 10, marginBottom: 14
            }}>
              {[
                {
                  label: "Taxable Value",
                  val: `₹ ${summary.taxable.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                  bg: "linear-gradient(135deg,#3b82f6,#2563eb)"
                },
                {
                  label: "CGST + SGST",
                  val: `₹ ${(summary.cgst + summary.sgst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                  sub: `C: ₹${summary.cgst.toFixed(2)}  |  S: ₹${summary.sgst.toFixed(2)}`,
                  bg: "linear-gradient(135deg,#14b8a6,#047857)"
                },
                {
                  label: "IGST Amount",
                  val: `₹ ${summary.igst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                  bg: "linear-gradient(135deg,#f59e0b,#b45309)"
                },
                {
                  label: reportType === "CDNR" || reportType === "CDNUR"
                    ? "Total Note Value" : "Total Invoice Value",
                  val: `₹ ${summary.totalValue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                  sub: `${summary.docCount} ${
                    reportType === "B2B" || reportType === "B2BUR" ? "Bills" :
                    reportType === "CDNR" || reportType === "CDNUR" ? "Notes" : "Records"
                  }`,
                  bg: "linear-gradient(135deg,#8b5cf6,#6d28d9)"
                },
                // ITC card — sirf B2B aur CDNR mein
                ...(reportType === "B2B" || reportType === "CDNR" ? [{
                  label: reportType === "CDNR" ? "ITC Reversal" : "Total ITC Available",
                  val: `₹ ${(summary.cgst + summary.sgst + summary.igst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                  sub: reportType === "CDNR" ? "To be reversed" : "Eligible to claim",
                  bg: reportType === "CDNR"
                    ? "linear-gradient(135deg,#ef4444,#b91c1c)"
                    : "linear-gradient(135deg,#10b981,#047857)"
                }] : []),
              ].map((card, i) => (
                <div key={i} style={{
                  padding: "12px 14px", borderRadius: 8, color: "white",
                  background: card.bg, boxShadow: "0 2px 6px rgba(0,0,0,0.12)"
                }}>
                  <div style={{
                    fontSize: 10, opacity: 0.9,
                    textTransform: "uppercase", letterSpacing: "0.5px"
                  }}>
                    {card.label}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, marginTop: 3 }}>
                    {card.val}
                  </div>
                  {card.sub && (
                    <div style={{ fontSize: 10, opacity: 0.75, marginTop: 4 }}>
                      {card.sub}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Data Table ── */}
          <div style={{ border: "1px solid #e0e6ed", borderRadius: 6, overflow: "hidden" }}>
            <div style={{ overflowX: "auto", maxHeight: 480 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={thStyle("#f8fafc", "center", false)}>#</th>
                    {columns.map(col => (
                      <th
                        key={col}
                        style={thStyle(
                          isItcCol(col) ? "#fef3c7" : "#f8fafc",
                          isNumericCol(col) ? "right" : "left",
                          isItcCol(col)
                        )}
                      >
                        {col.replace(/_/g, " ")}
                        {isItcCol(col) && (
                          <span style={{
                            fontSize: 9, background: "#f59e0b",
                            color: "white", borderRadius: 3,
                            padding: "1px 4px", marginLeft: 4
                          }}>
                            ITC
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}
                      >
                        <i className="bi bi-hourglass-split me-2"></i>
                        Fetching GSTR-2 Data...
                      </td>
                    </tr>
                  ) : reportData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}
                      >
                        <i className="bi bi-inbox me-2"></i>
                        No records. Select date range and click View Report.
                      </td>
                    </tr>
                  ) : (
                    reportData.map((row, ri) => (
                      <tr
                        key={ri}
                        style={{ background: ri % 2 === 0 ? "white" : "#fafafa" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"}
                        onMouseLeave={e =>
                          e.currentTarget.style.background = ri % 2 === 0 ? "white" : "#fafafa"
                        }
                      >
                        <td style={tdStyle("center", true)}>{ri + 1}</td>
                        {columns.map(col => {
                          const val   = row[col];
                          const isNum = typeof val === "number";
                          const isDoc =
                            col === "Invoice_Number" ||
                            col === "Note_Number";
                          return (
                            <td
                              key={col}
                              style={{
                                ...tdStyle(isNum ? "right" : "left", false),
                                fontWeight: isNum ? 600 : 400,
                                color: isDoc
                                  ? "#2563eb"
                                  : isItcCol(col)
                                    ? "#92400e"
                                    : (isNum ? "#1e293b" : "#374151"),
                                background: isItcCol(col)
                                  ? "rgba(254, 243, 199, 0.4)" : "transparent"
                              }}
                            >
                              {isNum
                                ? val.toLocaleString("en-IN", { minimumFractionDigits: 2 })
                                : (val || "—")
                              }
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>

                {/* ── Footer Totals ── */}
                {reportData.length > 0 && (
                  <tfoot>
                    <tr style={{ background: "#1e293b", color: "white" }}>
                      <td style={{ padding: "7px 10px", fontWeight: 700, textAlign: "center" }}>
                        Σ
                      </td>
                      {columns.map(col => {
                        const isNum = isNumericCol(col);
                        const total = isNum
                          ? reportData.reduce((s, r) => s + Number(r[col] || 0), 0)
                          : null;
                        return (
                          <td
                            key={col}
                            style={{
                              padding: "7px 10px",
                              textAlign: isNum ? "right" : "left",
                              fontWeight: 700, fontSize: 12,
                              color: isItcCol(col) ? "#fcd34d" : "white"
                            }}
                          >
                            {isNum
                              ? total.toLocaleString("en-IN", { minimumFractionDigits: 2 })
                              : ""
                            }
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Record count */}
          {reportData.length > 0 && (
            <div style={{
              marginTop: 8, fontSize: 11, color: "#64748b", textAlign: "right"
            }}>
              {reportData.length} records
              {(reportType === "B2B" || reportType === "B2BUR") &&
                ` across ${summary.docCount} invoices`}
              {(reportType === "CDNR" || reportType === "CDNUR") &&
                ` across ${summary.docCount} debit notes`}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ── Style helpers ──
const thStyle = (bg, align, highlight) => ({
  background: highlight ? "#fef3c7" : bg,
  color: highlight ? "#92400e" : "#334155",
  padding: "8px 10px", fontWeight: 600,
  borderBottom: "2px solid #e2e8f0",
  textAlign: align, whiteSpace: "nowrap",
  position: "sticky", top: 0, zIndex: 1,
  fontSize: 12
});

const tdStyle = (align, muted) => ({
  padding: "6px 10px",
  borderBottom: "1px solid #f1f5f9",
  textAlign: align,
  color: muted ? "#64748b" : "#1e293b",
  verticalAlign: "middle"
});

export default GSTR2Report;